import React from 'react'

import { Alert, Box, Button } from '@factorialco/deprecated-design-system'
import { DocumentsEmployeeFolder } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import FolderSelector from 'modules/documents/components/Folders/FolderSelector'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import useDocumentContextlessPolicies from 'modules/documents/hooks/useDocumentContextlessPolicies'
import { useGetEmployeeFolder } from 'modules/documents/hooks/useGetEmployeeFolder'
import { Folder } from 'modules/documents/hooks/useGetFolders'

const getCommonElements = (arrays: number[][]): number[] | undefined => {
  if (arrays.length <= 1) return arrays.length === 1 ? arrays[0] : []

  const referenceArray = arrays[0]

  if (!referenceArray) return undefined

  const commonElements = referenceArray.filter((element) =>
    arrays.every((array) => array.includes(element))
  )

  return commonElements
}

const foldersInCommon = (employeeIds: number[], employeeFolders: DocumentsEmployeeFolder[]) => {
  const processedEmployeeFoldersIds = employeeIds.map((employeeId) => {
    return employeeFolders
      .filter((employeeFolder) => employeeFolder.employeeId === employeeId)
      .map((employeeFolder) => employeeFolder.folderId)
  })

  const commonFoldersIds = getCommonElements(processedEmployeeFoldersIds)

  if (commonFoldersIds === undefined) return []

  const commonFoldersWithDuplicates = employeeFolders
    .filter((employeeFolder) => commonFoldersIds.includes(employeeFolder.folderId))
    .map((elem) => elem.folder)
    .filter(Boolean)

  return [...new Set(commonFoldersWithDuplicates)]
}

const extractEmployeeIdsFromDocuments = (documents: Document[]) => {
  return documents.map((doc) => doc?.employee?.id).filter(Boolean)
}

const defineInitialFolder = (docs: Document[], folders: Folder[]): Folder | undefined => {
  const folder = docs[0]?.folder

  if (folder) {
    return folders.find((elem) => elem.id === folder.id) || folders[0]
  }

  return undefined
}

interface Props {
  documents: Document[]
  loading?: boolean
  onSubmit: (selectedFolderId: number | null) => void
  onClose: () => void
}

const UpdateFolderModal = ({ documents, loading, onSubmit, onClose }: Props) => {
  const { policies } = useDocumentContextlessPolicies()
  const [selectedFolderId, setSelectedFolderId] = React.useState<number | null>(null)

  const multiDocuments = useUnsafeMemo(() => {
    return documents.length > 1
  }, [documents])

  const employeeIds = useUnsafeMemo(() => {
    return extractEmployeeIdsFromDocuments(documents)
  }, [documents])

  const { employeeFolders, loading: loadingEmployeeFolder } = useGetEmployeeFolder(employeeIds)

  const editableEmployeeFolders = useUnsafeMemo(() => {
    return employeeFolders?.filter((employee) => employee._policies?.edit.can)
  }, [employeeFolders])

  const commonFolders = useUnsafeMemo(() => {
    return editableEmployeeFolders && employeeIds
      ? foldersInCommon(
          employeeIds,
          editableEmployeeFolders.filter((ef): ef is DocumentsEmployeeFolder => !!ef)
        )
      : []
  }, [editableEmployeeFolders, employeeIds])

  const folders = useUnsafeMemo(() => {
    return commonFolders.filter((folder) => !!folder)
  }, [commonFolders])

  const initialFolder = useUnsafeMemo(() => {
    return folders ? defineInitialFolder(documents, folders) : undefined
  }, [folders, documents])

  const canManageDefaultEmployeeFolder = useUnsafeMemo(() => {
    return policies?.employeeManageDefaultEmployeeFolder.can
  }, [policies])

  const anyDestinationFolder = useUnsafeMemo(() => {
    return folders.length > 0 || canManageDefaultEmployeeFolder
  }, [folders, canManageDefaultEmployeeFolder])

  useUnsafeEffect(() => {
    if (initialFolder) setSelectedFolderId(initialFolder?.id)
  }, [initialFolder])

  const isLoading = useUnsafeMemo(() => {
    return loading || loadingEmployeeFolder
  }, [loading, loadingEmployeeFolder])

  const filename = useUnsafeMemo(() => {
    return documents[0] && documents.length > 0 ? documents[0].filename : ''
  }, [documents])

  return (
    <>
      {employeeFolders && (
        <Modal
          illustration="file-move"
          title={i18n.tp('documents.employee.bulk-actions.update-folder.title', {
            count: documents.length,
            filename: filename,
          })}
          description={i18n.tp('documents.employee.bulk-actions.update-folder.description', {
            count: documents.length,
          })}
          onSubmit={() => onSubmit(selectedFolderId)}
          onClose={onClose}
          footer={
            <>
              <Button type="secondary" onClick={onClose}>
                {i18n.t('documents.employee.bulk-actions.update-folder.cancel')}
              </Button>
              <Button
                submit
                iconName={isLoading ? 'folder' : undefined}
                loading={isLoading}
                disabled={!anyDestinationFolder}
              >
                {i18n.t('documents.employee.bulk-actions.update-folder.submit')}
              </Button>
            </>
          }
        >
          <Box gap="s16">
            {anyDestinationFolder && (
              <FolderSelector
                folders={folders}
                initialFolder={initialFolder}
                label={i18n.t('split_documents.assign_documents.destination_folder')}
                onChange={setSelectedFolderId}
                noneOption={canManageDefaultEmployeeFolder ?? false}
                aria-label={i18n.t('documents.employee.bulk-actions.update-folder.selector')}
              />
            )}
            {multiDocuments && (
              <Alert color="warning">
                {i18n.t('documents.employee.bulk-actions.update-folder.alert-datatable')}
              </Alert>
            )}
          </Box>
        </Modal>
      )}
    </>
  )
}

export default UpdateFolderModal
